import React from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown } from "lucide-react";
import backgroundImage from "../../assets/Vector 17.svg";

interface StatsCardProps {
    title: string;
    lastMonthValue: string | number;
    thisMonthValue: string | number;
    multiplier: string;
    percentage: string;
    isIncrease: boolean;
    themeColor: "green" | "teal" | "orange" | "purple";
    variants?: any;
}

const colorConfigs = {
    green: {
       bg: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
        text: "#2c4a3e",
        pill: "#a0c49d",
        pillText: "#ffffff",
        accent: "#608a61",
    },
    teal: {
        bg: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
        text: "#204a52",
        pill: "#5d9099",
        pillText: "#ffffff",
        accent: "#407e88",
    },
    orange: {
        bg: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
        text: "#5c3325",
        pill: "#ff9175",
        pillText: "#ffffff",
        accent: "#f46f4e",
    },
    purple: {
        bg: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
        text: "#2b2b5c",
        pill: "#a1a1ff",
        pillText: "#ffffff",
        accent: "#7676ff",
    },
};

const StatsCard: React.FC<StatsCardProps> = ({
    title,
    lastMonthValue,
    thisMonthValue,
    multiplier,
    percentage,
    isIncrease,
    themeColor,
    variants,
}) => {
    const config = colorConfigs[themeColor];

    return (
        <motion.div variants={variants}>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: "20px",
                    background: config.bg,
                    position: "relative",
                    overflow: "hidden",
                    border: "none",
                    minHeight: "155px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    "&:hover": {
                        transform: "translateY(-8px) scale(1.02)",
                        boxShadow: `0 20px 40px ${alpha(config.accent, 0.12)}`,
                        "& .mountain-shape": {
                            transform: "scale(1.1) rotate(-5deg)",
                            opacity: 0.2,
                        }
                    },
                }}
            >
                {/* Large Subtle Mountain Shape (Vector Image) */}
              <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "130%",
          width: "70%",
          zIndex: 0,
        }}
      >
        <img
          src={backgroundImage}
          alt="background"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </div>

                <Box sx={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: config.text,
                            fontWeight: 700,
                            mb: 2,
                            fontSize: "1.05rem",
                            letterSpacing: "-0.2px",
                        }}
                    >
                        {title}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            mt: "auto",
                        }}
                    >
                        {/* Last Month */}
                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    color: config.text,
                                    mb: 0.5,
                                    fontSize: "1.85rem",
                                    lineHeight: 1,
                                }}
                            >
                                {lastMonthValue}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: alpha(config.text, 0.5),
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Last Month
                            </Typography>
                        </Box>

                        {/* Trend Indicator */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                px: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.2,
                                    mb: 0.8,
                                    color: alpha(config.text, 0.7),
                                }}
                            >
                                {isIncrease ? (
                                    <TrendingUp size={20} strokeWidth={2.5} style={{ color: alpha(config.accent, 0.8) }} />
                                ) : (
                                    <TrendingDown size={20} strokeWidth={2.5} style={{ color: alpha(config.accent, 0.8) }} />
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800, fontSize: "0.9rem", ml: 0.5 }}
                                >
                                    {multiplier}
                                </Typography>
                            </Box>
                            
                            <Box
                                sx={{
                                    bgcolor: config.pill,
                                    px: 1.5,
                                    py: 0.35,
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                {isIncrease ? (
                                    <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: `6px solid ${config.pillText}` }} />
                                ) : (
                                    <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: `6px solid ${config.pillText}` }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: config.pillText,
                                        fontWeight: 800,
                                        fontSize: "0.7rem",
                                        letterSpacing: "0.2px",
                                    }}
                                >
                                    {percentage}
                                </Typography>
                            </Box>
                        </Box>

                        {/* This Month */}
                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    color: config.text,
                                    mb: 0.5,
                                    fontSize: "1.85rem",
                                    lineHeight: 1,
                                }}
                            >
                                {thisMonthValue}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: alpha(config.text, 0.5),
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                This Month
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </motion.div>
    );
};

export default StatsCard;
